import { ConfirmDialog, PageBanner, Spinner } from "@/components/reusable";
import { SchemaCard } from "@/components/schema-builder/SchemaCard";
import { ProviderSelector } from "@/components/providers/ProviderSelector";
import { isUsingMocks } from "@/config/app";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useSchemaPermissions } from "@/hooks/useSchemaPermissions";
import {
  buildSchemaToProvidersMap,
  getNextCopyName,
  getProviderPillsForSchema,
  type SchemaAssignedProvider,
} from "@/lib/schemaBuilderHelpers";
import {
  clearEditSession,
  clearSchemaActivityLog,
  deleteCustomSchema,
  getCustomSchemas,
  getSchemasCache,
  setSchemasCache,
} from "@/lib/schemaBuilderStorage";
import {
  archiveSchema,
  deleteDraftSchema,
  duplicateSchema,
  listSchemas,
  publishSchema,
} from "@/services/schemaBuilderApi";
import {
  fetchProvidersCached,
  fetchSchemaAssignments,
  invalidateProvidersCache,
} from "@/services/providersApi";
import type { SchemaSummary, SchemaStatus, SchemaStatusFilterValue } from "@/types/schemaBuilder";
import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const BASE_FILTERS: { label: string; value: SchemaStatusFilterValue }[] = [
  { label: "All", value: "all" },
  { label: "Drafts", value: "draft" },
  { label: "Published", value: "published" },
  { label: "Preset", value: "preset" },
  { label: "Archived", value: "archived" },
];

function getStatusFilters(): { label: string; value: SchemaStatusFilterValue }[] {
  return [
    BASE_FILTERS[0]!,
    { label: "Active for Provider", value: "active_for_provider" },
    ...BASE_FILTERS.slice(1),
  ];
}

/**
 * Status-tab match for Schema Manager list cards.
 * Active for Provider ignores status — PSA membership is checked separately.
 */
function matchesStatusFilter(
  schema: SchemaSummary,
  statusFilter: SchemaStatusFilterValue
): boolean {
  if (statusFilter === "all" || statusFilter === "active_for_provider") return true;
  return schema.status === statusFilter;
}

/**
 * Provider visibility for Schema Manager.
 * - Active for Provider + provider selected: PSA for that provider.
 * - Active for Provider + no selection: admins see all PSA-assigned schemas; others must pick.
 * - Preset tab: shared catalogue — ignore the selector (status filter limits to presets).
 * - App.ADMIN + no selection (other tabs): browse the full API-returned set.
 * - Non-admin + no selection: hide everything until a provider is chosen.
 * - Provider selected (non-Preset / non-Active tabs): ownership plus shared presets (`providerId === null`).
 */
function matchesProviderFilter(
  schema: SchemaSummary,
  statusFilter: SchemaStatusFilterValue,
  providerId: string | null,
  isAdmin: boolean,
  assignedSchemaIds: Set<string>,
  allAssignedSchemaIds: Set<string>
): boolean {
  if (statusFilter === "active_for_provider") {
    if (!providerId) return isAdmin && allAssignedSchemaIds.has(schema.id);
    return assignedSchemaIds.has(schema.id);
  }

  if (statusFilter === "preset") return true;
  if (!providerId) return isAdmin;
  // Owned by selected provider, or shared presets (null provider_id).
  return schema.providerId === providerId || schema.providerId === null;
}

export function SchemaBuilderPage() {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { canWrite, canPublish, canArchive, canDelete } = useSchemaPermissions();
  const isAdmin = Boolean(currentUser?.roles.includes("App.ADMIN"));
  const statusFilters = useMemo(() => getStatusFilters(), []);

  const [statusFilter, setStatusFilter] = useState<SchemaStatusFilterValue>("all");
  const [providerId, setProviderId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [schemas, setSchemas] = useState<SchemaSummary[]>(() => getSchemasCache() ?? []);
  const [schemaToProviders, setSchemaToProviders] = useState<Map<string, SchemaAssignedProvider[]>>(
    () => new Map()
  );
  /** Direct PSA ids for the selected provider (Active tab + selected-provider pills). */
  const [assignedSchemaIdsForProvider, setAssignedSchemaIdsForProvider] = useState<Set<string>>(
    () => new Set()
  );
  /** True until the reverse PSA index (fetchProvidersCached) settles. */
  const [isPsaIndexLoading, setIsPsaIndexLoading] = useState(true);
  /** True while fetchSchemaAssignments is in flight for the selected provider. */
  const [isAssignmentsLoading, setIsAssignmentsLoading] = useState(false);
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());
  const [publishingIds, setPublishingIds] = useState<Set<string>>(new Set());
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(() => !getSchemasCache());
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Tracks in-flight optimistic status changes so the background re-fetch doesn't
  // race and overwrite them with stale data.
  const optimisticStatusRef = useRef<Record<string, SchemaStatus>>({});

  useEffect(() => {
    // Re-fetch in the background (incl. archived, so the Archived tab works and
    // status changes persist across refreshes). listSchemas retries transient 5xx.
    listSchemas(true)
      .then((result) => {
        // Preserve any optimistic status overrides that are still in-flight
        const overrides = optimisticStatusRef.current;
        // Apply local column/rule count overrides for schemas with unsaved edits
        const localById = new Map(getCustomSchemas().map((s) => [s.id, s]));
        const merged = result.map((s) => {
          const local = localById.get(s.id);
          return {
            ...s,
            ...(local
              ? {
                  totalColumns: local.totalColumns ?? s.totalColumns,
                  rules: local.rules ?? s.rules,
                  conflicts: local.conflicts ?? s.conflicts,
                }
              : {}),
            ...(overrides[s.id] !== undefined ? { status: overrides[s.id] } : {}),
          };
        });
        setSchemasCache(merged);
        setSchemas((prev) => {
          // Keep any pending-duplicate placeholders that haven't resolved yet
          const pendingPlaceholders = prev.filter((s) => s.id.startsWith("__pending__"));
          return [...pendingPlaceholders, ...merged];
        });

        // Drop the override now that fresh backend data is in; mock mode uses the
        // same store as its actual schema persistence, so leave those alone.
        if (!isUsingMocks()) {
          for (const s of result) {
            if (localById.has(s.id)) deleteCustomSchema(s.id);
          }
        }
      })
      .catch((err: Error) => {
        // Only surface the error if we have nothing to show
        if (!getSchemasCache()) setError(err.message);
      })
      .finally(() => setIsLoading(false));
  }, []);

  // Reverse PSA index for pills when no provider is selected (API max limit is 100).
  useEffect(() => {
    setIsPsaIndexLoading(true);
    fetchProvidersCached()
      .then((res) => setSchemaToProviders(buildSchemaToProvidersMap(res.items)))
      .catch(() => {
        // Pills degrade gracefully; Active tab uses fetchSchemaAssignments below.
        setSchemaToProviders(new Map());
      })
      .finally(() => setIsPsaIndexLoading(false));
  }, []);

  // Active for Provider + selected-provider pills: same PSA endpoint as Upload.
  useEffect(() => {
    if (!providerId) {
      setAssignedSchemaIdsForProvider(new Set());
      setIsAssignmentsLoading(false);
      return;
    }
    let cancelled = false;
    setIsAssignmentsLoading(true);
    fetchSchemaAssignments(providerId)
      .then((assignments) => {
        if (!cancelled) {
          setAssignedSchemaIdsForProvider(new Set(assignments.map((a) => a.schema_id)));
        }
      })
      .catch(() => {
        if (!cancelled) setAssignedSchemaIdsForProvider(new Set());
      })
      .finally(() => {
        if (!cancelled) setIsAssignmentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [providerId]);

  /** Schema IDs with ≥1 PSA assignment across providers (admin Active tab, no selection). */
  const allAssignedSchemaIds = useMemo(
    () => new Set(schemaToProviders.keys()),
    [schemaToProviders]
  );

  // Provider + status scope for the counter denominator (search does not affect it).
  const scopedTotal = useMemo(() => {
    return schemas.filter(
      (schema) =>
        matchesStatusFilter(schema, statusFilter) &&
        matchesProviderFilter(
          schema,
          statusFilter,
          providerId,
          isAdmin,
          assignedSchemaIdsForProvider,
          allAssignedSchemaIds
        )
    ).length;
  }, [
    schemas,
    statusFilter,
    providerId,
    isAdmin,
    assignedSchemaIdsForProvider,
    allAssignedSchemaIds,
  ]);

  const filteredSchemas = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return schemas.filter((schema) => {
      if (!matchesStatusFilter(schema, statusFilter)) return false;
      if (
        !matchesProviderFilter(
          schema,
          statusFilter,
          providerId,
          isAdmin,
          assignedSchemaIdsForProvider,
          allAssignedSchemaIds
        )
      ) {
        return false;
      }
      if (query.length === 0) return true;
      return (
        schema.name.toLowerCase().includes(query) ||
        schema.description.toLowerCase().includes(query)
      );
    });
  }, [
    schemas,
    statusFilter,
    searchQuery,
    providerId,
    isAdmin,
    assignedSchemaIdsForProvider,
    allAssignedSchemaIds,
  ]);

  /**
   * Empty-grid copy when filters (not a fetch error) hide every card.
   * Distinguishes “pick a provider”, “this provider owns nothing”, and empty tabs.
   */
  function getEmptyListMessage(): string | null {
    if (filteredSchemas.length > 0) return null;
    if (schemas.length === 0) return "No schemas available.";
    if (statusFilter === "active_for_provider") {
      if (!providerId) {
        return isAdmin
          ? "No schemas are active for any provider."
          : "Select a provider to view active schemas.";
      }
      return "No schemas are active for this provider.";
    }
    if (!providerId && !isAdmin) {
      return "Select a provider to view schemas.";
    }
    if (providerId && statusFilter !== "preset" && scopedTotal === 0) {
      return "No schemas owned by this provider.";
    }
    return "No schemas match this filter.";
  }

  function handleDuplicateSchema(schemaId: string) {
    const source = schemas.find((s) => s.id === schemaId);
    if (!source) return;
    if (!providerId) {
      setError("Select a provider before duplicating a schema.");
      return;
    }

    const newName = getNextCopyName(source.name, schemas);
    const tempId = `__pending__${Date.now()}`;
    const placeholder: SchemaSummary = {
      ...source,
      id: tempId,
      name: newName,
      status: "draft",
      conflicts: 0,
      createdAt: new Date().toISOString(),
      lastEditedAt: new Date().toISOString(),
    };

    setSchemas((prev) => [placeholder, ...prev]);
    setPendingIds((prev) => new Set(prev).add(tempId));

    duplicateSchema(schemaId, newName, providerId ?? undefined)
      .then((created) => {
        setSchemas((prev) => {
          const updated = prev.map((s) => (s.id === tempId ? created : s));
          setSchemasCache(updated);
          return updated;
        });
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(tempId);
          return next;
        });
      })
      .catch((err: Error) => {
        setSchemas((prev) => prev.filter((s) => s.id !== tempId));
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(tempId);
          return next;
        });
        setError(err.message);
      });
  }

  function handlePublishSchema(schemaId: string) {
    const original = schemas.find((s) => s.id === schemaId);
    if (!original) return;

    // Flip the badge to Published only once the backend confirms, not optimistically.
    setPublishingIds((prev) => new Set(prev).add(schemaId));
    setPendingIds((prev) => new Set(prev).add(schemaId));

    publishSchema(schemaId)
      .then(async () => {
        setSchemas((prev) => {
          const updated = prev.map((s) =>
            s.id === schemaId ? { ...s, status: "published" as const } : s
          );
          setSchemasCache(updated);
          return updated;
        });

        // Publish now auto-creates a PSA for the owning provider. Refresh both
        // the reverse index (pills) and the selected-provider assignment set so
        // "available for upload" UI updates without a full page reload.
        invalidateProvidersCache();
        try {
          const providersRes = await fetchProvidersCached();
          setSchemaToProviders(buildSchemaToProvidersMap(providersRes.items));
        } catch {
          // Pills can stay stale; upload picker refetches PSA independently.
        }
        if (providerId) {
          try {
            const assignments = await fetchSchemaAssignments(providerId);
            setAssignedSchemaIdsForProvider(new Set(assignments.map((a) => a.schema_id)));
          } catch {
            // Active-tab / selected-provider pills degrade until next provider change.
          }
        }
      })
      .catch((err: Error) => {
        setError(err.message);
      })
      .finally(() => {
        setPublishingIds((prev) => {
          const next = new Set(prev);
          next.delete(schemaId);
          return next;
        });
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(schemaId);
          return next;
        });
      });
  }

  function handleArchiveSchema(schemaId: string) {
    const original = schemas.find((s) => s.id === schemaId);
    if (!original) return;

    optimisticStatusRef.current[schemaId] = "archived";
    setSchemas((prev) => {
      const updated = prev.map((s) =>
        s.id === schemaId ? { ...s, status: "archived" as const } : s
      );
      setSchemasCache(updated);
      return updated;
    });
    archiveSchema(schemaId)
      .then(() => {
        delete optimisticStatusRef.current[schemaId];
      })
      .catch((err: Error) => {
        delete optimisticStatusRef.current[schemaId];
        setSchemas((prev) => {
          const updated = prev.map((s) =>
            s.id === schemaId ? { ...s, status: original.status } : s
          );
          setSchemasCache(updated);
          return updated;
        });
        setError(err.message);
      });
  }

  function handleDeleteSchema(schemaId: string) {
    const original = schemas.find((s) => s.id === schemaId);
    if (!original) return;

    setPendingIds((prev) => new Set(prev).add(schemaId));
    setDeletingIds((prev) => new Set(prev).add(schemaId));

    deleteDraftSchema(schemaId)
      .then(() => {
        clearEditSession(schemaId);
        clearSchemaActivityLog(schemaId);
        deleteCustomSchema(schemaId);
        setSchemas((prev) => {
          const updated = prev.filter((s) => s.id !== schemaId);
          setSchemasCache(updated);
          return updated;
        });
      })
      .catch((err: Error) => {
        setError(err.message);
      })
      .finally(() => {
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(schemaId);
          return next;
        });
        setDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(schemaId);
          return next;
        });
      });
  }

  const emptyListMessage = getEmptyListMessage();

  // PSA-dependent UI: avoid flashing empty Active tab / missing pills while fetches settle.
  const isProviderResultsLoading = isPsaIndexLoading || isAssignmentsLoading;
  const isActiveTabPsaLoading =
    statusFilter === "active_for_provider" &&
    (providerId ? isAssignmentsLoading : isAdmin && isPsaIndexLoading);

  return (
    <>
      <div className="animate-fade-in">
        <PageBanner
          title="Schema Manager"
          subtitle="Create, customize, and publish schemas"
          className="!mb-8 !h-30"
        />

        <div className="mb-5 flex items-center justify-between">
          <div className="flex gap-8">
            {statusFilters.map((filter) => (
              <button
                key={filter.value}
                type="button"
                onClick={() => setStatusFilter(filter.value)}
                className={`rounded-t-md border-b-2 px-3 pb-3 pt-2 text-sm transition ${
                  statusFilter === filter.value
                    ? "border-[var(--color-brand-primary)] text-[var(--color-text-primary)]"
                    : "border-transparent text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {canWrite && (
            <button
              type="button"
              onClick={() => navigate("/schema-builder/create")}
              className="rounded-md bg-[var(--color-action-primary)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[var(--color-action-primary-hover)]"
            >
              Create New Schema
            </button>
          )}
        </div>

        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-[280px] items-center gap-2 rounded-md border border-[var(--color-border)] bg-white px-3">
              <Search className="h-4 w-4 text-[var(--color-text-tertiary)]" />
              <input
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search schemas..."
                className="w-full text-sm outline-none placeholder:text-[var(--color-text-tertiary)]"
              />
            </div>

            <div className="flex items-center gap-2">
              <ProviderSelector
                value={providerId}
                onChange={(id) => {
                  setProviderId(id);
                  if (id) setError(null);
                }}
                className="w-[220px]"
              />
              {isProviderResultsLoading && (
                <span
                  className="flex items-center gap-1.5 text-sm text-[var(--color-text-tertiary)]"
                  aria-live="polite"
                >
                  <Spinner size="sm" />
                  {isAssignmentsLoading ? "Loading assignments…" : "Loading providers…"}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-[var(--color-text-primary)]">
              Showing {filteredSchemas.length} of {scopedTotal}
            </span>
          </div>
        </div>

        {(isLoading || isActiveTabPsaLoading) && (
          <div className="flex items-center justify-center gap-2 py-20 text-sm text-[var(--color-text-secondary)]">
            <Spinner size="sm" />
            {isLoading ? "Loading schemas…" : "Loading active schemas…"}
          </div>
        )}

        {!isLoading && !isActiveTabPsaLoading && (
          <>
            {error && (
              <div className="mb-6 rounded-md border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-600">
                {error}
              </div>
            )}
            {emptyListMessage ? (
              <div className="py-20 text-center text-sm text-[var(--color-text-secondary)]">
                {emptyListMessage}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-8">
                {filteredSchemas.map((schema) => (
                  <SchemaCard
                    key={schema.id}
                    schema={schema}
                    assignedProviders={getProviderPillsForSchema(
                      schema.id,
                      schemaToProviders,
                      providerId
                    )}
                    onEdit={() =>
                      navigate(`/schema-builder/${schema.id}`, {
                        state: { source: "edit", schemaName: schema.name },
                      })
                    }
                    onDuplicate={() => handleDuplicateSchema(schema.id)}
                    onPublish={() => handlePublishSchema(schema.id)}
                    onArchive={() => handleArchiveSchema(schema.id)}
                    onDelete={() => setDeleteConfirmId(schema.id)}
                    pending={pendingIds.has(schema.id)}
                    pendingLabel={
                      deletingIds.has(schema.id)
                        ? "Deleting…"
                        : publishingIds.has(schema.id)
                          ? "Publishing…"
                          : "Creating…"
                    }
                    canWrite={canWrite}
                    canPublish={canPublish}
                    canArchive={canArchive}
                    canDelete={canDelete}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmDialog
        open={deleteConfirmId !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmId(null);
        }}
        tone="danger"
        title="Delete draft?"
        description={
          <>
            This will permanently delete{" "}
            <strong>{schemas.find((s) => s.id === deleteConfirmId)?.name ?? "this draft"}</strong>.
            This action cannot be undone.
          </>
        }
        confirmLabel="Delete"
        onConfirm={() => {
          if (deleteConfirmId) handleDeleteSchema(deleteConfirmId);
          setDeleteConfirmId(null);
        }}
      />
    </>
  );
}
